import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "../../../configs/apiRoutes";
import instance from "../../../api/apiClient";
import {
  GameItem,
  mergeGameItem,
  updateGame,
} from "../../gameCenter/games/slice";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface WagerItem {
  id?: number;
  title: string;
  contribution: number;
  description: string;
  isActive: boolean;
}

interface WagersState {
  wagersList: WagerItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  selectedItemsGames: number[];
  payload?: WagerItem;
  gamesList: GameItem[];
  activePageGames: number;
  totalPagesGames: number;
  totalItemsGames: number;
  isListGamesFetched: boolean;
  isItemFetching: boolean;
  processingItemId?: WagerItem["id"];
  processingGameItemId?: GameItem["id"];
}
interface GetListPayload {
  limit: number;
  offset: number;
}
interface GetGamesListPayload {
  limit: number;
  offset: number;
  providerIds: number[];
  gameTitle?: string;
}
export interface WagerBunchPayload {
  ids: number[];
  isActive: number;
}

export const updateWagersBunch = createAsyncThunk(
  "wagers/update-bunch",
  async (data: WagerBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.wagerGamesUpdateBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const getGamesList = createAsyncThunk(
  "wagers/games/list",
  async (data: GetGamesListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.wagerGames}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getWagersList = createAsyncThunk(
  "wagers/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.get(
        `${apiRoutes.wagersList}?limit=${data.limit}&offset=${data.offset}`,
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getWagerItem = createAsyncThunk(
  "wagers/item",
  async (id: number) => {
    try {
      const result = await instance.get(`${apiRoutes.wagerItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createWager = createAsyncThunk<
  number,
  WagerItem,
  { rejectValue: ApiErrorPayload }
>("wagers/create", async (data, { rejectWithValue }) => {
  try {
    const body = {
      ...data,
      isActive: Number(data.isActive),
    };

    const result = await instance.post(`${apiRoutes.wagerCreate}`, body);
    return result.data.id;
  } catch (error: any) {
    const apiError = error?.data?.error ?? error?.data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});
export const updateWager = createAsyncThunk(
  "wagers/update",
  async (data: WagerItem) => {
    try {
      const body = {
        ...data,
        isActive: Number(data.isActive),
      };

      await instance.put(`${apiRoutes.wagerEdit}/${data.id}`, body);
      return true;
    } catch (error) {
      return false;
    }
  }
);
export const deleteWagerItem = createAsyncThunk(
  "wagers/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.wagerDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const removeWagerGames = createAsyncThunk(
  "wagers/games/remove",
  async ({ wagerId, gameIds }: { wagerId: number; gameIds: number[] }) => {
    try {
      const result = await instance.put(
        `${apiRoutes.wagerRemoveGames}/${wagerId}/remove-games`,
        { gameIds }
      );
      return result.data;
    } catch {
      return undefined;
    }
  }
);

const initialState: WagersState = {
  wagersList: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  selectedItemsGames: [],
  gamesList: [],
  isListGamesFetched: false,
  activePageGames: 1,
  totalItemsGames: 0,
  totalPagesGames: 0,
  isItemFetching: false,
};

export const wagersSlice = createSlice({
  name: "wagers",
  initialState,
  reducers: {
    handleUpdateFields: (
      state: WagersState,
      action: PayloadAction<WagerItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: WagersState) => {
      state.payload = undefined;
      state.isItemFetched = false;
    },
    handlePaginate: (state: WagersState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleInfoClear: (state: WagersState) => {
      state.payload = undefined;
      state.isItemFetched = false;
    },
    handleSelectItem: (state: WagersState, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: WagersState) => {
      if (state.selectedItems.length === state.wagersList.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.wagersList.map((el) => el.id ?? 0);
      }
    },
    handlePaginateGames: (
      state: WagersState,
      action: PayloadAction<number>,
    ) => {
      state.activePageGames = action.payload;
      state.isListGamesFetched = false;
      state.selectedItemsGames = [];
    },
    handleSelectItemGames: (
      state: WagersState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItemsGames.includes(action.payload)) {
        state.selectedItemsGames = state.selectedItemsGames.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItemsGames = [
          ...state.selectedItemsGames,
          action.payload,
        ];
      }
    },
    handleSelectAllGames: (state: WagersState) => {
      if (state.selectedItemsGames.length === state.gamesList.length) {
        state.selectedItemsGames = [];
      } else {
        state.selectedItemsGames = state.gamesList.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWagersList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.wagersList = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getGamesList.pending, (state) => {
      state.isListGamesFetched = false;
    });
    builder.addCase(getGamesList.fulfilled, (state, action) => {
      state.isListGamesFetched = true;
      state.gamesList = action.payload?.items ?? [];
      state.totalItemsGames = action.payload?.totalItems ?? 1;
      state.totalPagesGames = action.payload?.totalPages ?? 1;
    });
    builder.addCase(updateGame.pending, (state, action) => {
      state.processingGameItemId = action.meta.arg.id;
    });
    builder.addCase(updateGame.fulfilled, (state, action) => {
      state.gamesList = state.gamesList.map((game) =>
        game.id === action.meta.arg.id
          ? mergeGameItem(game, action.payload)
          : game,
      );
      state.processingGameItemId = undefined;
    });
    builder.addCase(updateGame.rejected, (state) => {
      state.processingGameItemId = undefined;
    });
    builder.addCase(getWagerItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload;
    });
    builder.addCase(createWager.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createWager.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createWager.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateWager.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateWager.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.wagersList = state.wagersList.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.processingItemId = undefined;
    });
    builder.addCase(updateWager.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteWagerItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deleteWagerItem.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteWagerItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updateWagersBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
  handleInfoClear,
  handlePaginateGames,
  handleSelectAllGames,
  handleSelectItemGames,
} = wagersSlice.actions;

// updateWagersBunch is already exported above
